import {
   Badge,
   Container,
   Group,
   Loader,
   Pagination,
   Paper,
   Stack,
   Text,
   ThemeIcon,
   Title,
   TextInput,
   Menu,
   ActionIcon,
   Table,
   ScrollArea,
   Select,
   Button,
   Modal,
} from "@mantine/core";
import {
   IconUser,
   IconCheck,
   IconX,
   IconSearch,
   IconDots,
   IconEye,
   IconUserCheck,
   IconUserX,
   IconFilter,
   IconTrash,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState, useMemo } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import { Endpoints } from "../../lib/endpoints";
import { usePerformRequest, PerformRequest } from "../../lib/usePerformRequest";
import { IPaginationProps, PaginatedResponse, NonPaginatedResponse } from "../../types";
import { IUserProps } from "../../types/User";
import { HttpCodes } from "../../lib/data";
import { useToastNotification } from "../../hooks/useToastNotification";

export default function Users() {
   const navigate = useNavigate();
   const { success, error: showError } = useToastNotification();
   const [searchQuery, setSearchQuery] = useState("");
   const [debouncedSearch] = useDebouncedValue(searchQuery, 500);
   const [statusFilter, setStatusFilter] = useState<string>("all");
   const [emailVerifiedFilter, setEmailVerifiedFilter] = useState<string>("all");
   const [phoneVerifiedFilter, setPhoneVerifiedFilter] = useState<string>("all");
   const [kycStatusFilter, setKycStatusFilter] = useState<string>("all");
   const [pagination, setPagination] = useState<IPaginationProps>({
      page: 1,
      limit: 20,
   });
   const [deleteModalOpened, setDeleteModalOpened] = useState(false);
   const [userToDelete, setUserToDelete] = useState<IUserProps | null>(null);
   const [isDeleting, setIsDeleting] = useState(false);

   const userEndpoint = useMemo(
      () =>
         Endpoints.GetUsers(
            pagination.page,
            pagination.limit,
            debouncedSearch || undefined,
            statusFilter,
            emailVerifiedFilter,
            phoneVerifiedFilter,
            kycStatusFilter
         ),
      [pagination.page, pagination.limit, debouncedSearch, statusFilter, emailVerifiedFilter, phoneVerifiedFilter, kycStatusFilter]
   );

   const {
      data: usersResponse,
      isLoading,
      reloadData,
   } = usePerformRequest<PaginatedResponse<IUserProps[]>>({
      method: "GET",
      route: userEndpoint.route,
   });

   // Reset to page 1 when filters change (but not pagination)
   useEffect(() => {
      setPagination((prev) => ({ ...prev, page: 1 }));
   }, [debouncedSearch, statusFilter, emailVerifiedFilter, phoneVerifiedFilter, kycStatusFilter]);

   const handleViewDetails = (userId: string) => {
      navigate(`/dashboard/users/${userId}`);
   };

   const handleDeleteClick = (user: IUserProps) => {
      setUserToDelete(user);
      setDeleteModalOpened(true);
   };

   const handleDeleteConfirm = async () => {
      if (!userToDelete) return;

      setIsDeleting(true);
      try {
         const response = await PerformRequest<NonPaginatedResponse<any>>({
            method: Endpoints.DeleteUser(userToDelete.id).method,
            route: Endpoints.DeleteUser(userToDelete.id).route,
         });

         if (response && response.status === HttpCodes.Success) {
            success("User deleted successfully");
            setDeleteModalOpened(false);
            setUserToDelete(null);
            // Reload the users list
            reloadData();
         } else {
            showError(response?.message || "Failed to delete user");
         }
      } catch (error: any) {
         showError(error?.message || "Failed to delete user");
      } finally {
         setIsDeleting(false);
      }
   };

   const formatDate = (timestamp: number) => {
      return new Date(timestamp).toLocaleDateString("en-US", {
         year: "numeric",
         month: "short",
         day: "numeric",
         hour: "2-digit",
         minute: "2-digit",
      });
   };

   const getStatusBadge = (status: string) => {
      const colors: Record<string, string> = {
         ACTIVE: "green",
         SUSPENDED: "red",
         DEACTIVATED: "gray",
      };
      return (
         <Badge color={colors[status] || "gray"} size="sm">
            {status}
         </Badge>
      );
   };

   const totalPages = usersResponse ? Math.ceil(usersResponse.totalRecords / usersResponse.limit) : 1;

   return (
      <Container size="xl" py="xl">
         <Stack gap="xl">
            {/* Header Section */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
               <Group justify="space-between" align="flex-end" mb="md">
                  <div>
                     <Title order={1} size="3rem" fw={800} style={{ color: "#679369", marginBottom: "0.5rem" }}>
                        Users Management
                     </Title>
                     <Text size="lg" c="dimmed">
                        View and manage all registered users
                     </Text>
                  </div>

                  {usersResponse?.data && usersResponse.data.length > 0 && (
                     <Paper
                        p="md"
                        radius="sm"
                        style={{
                           background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                           border: "1px solid #699269",
                        }}
                     >
                        <Group gap="xs">
                           <ThemeIcon size="lg" radius="sm" style={{ background: "#699269" }}>
                              <IconUser size={20} />
                           </ThemeIcon>
                           <div>
                              <Text size="xs" fw={500} c="#558B2F">
                                 Total Users
                              </Text>
                              <Text size="xl" fw={700} c="#699269">
                                 {usersResponse.totalRecords || 0}
                              </Text>
                           </div>
                        </Group>
                     </Paper>
                  )}
               </Group>

               {/* Search and Filters */}
               <Stack gap="md">
                  <TextInput
                     placeholder="Search by name, email, or phone..."
                     leftSection={<IconSearch size={18} />}
                     size="md"
                     value={searchQuery}
                     onChange={(e) => setSearchQuery(e.target.value)}
                     style={{ maxWidth: "500px" }}
                  />
                  <Group gap="md" align="flex-end">
                     <Select
                        label="Account Status"
                        placeholder="All statuses"
                        data={[
                           { value: "all", label: "All Statuses" },
                           { value: "ACTIVE", label: "Active" },
                           { value: "SUSPENDED", label: "Suspended" },
                           { value: "DEACTIVATED", label: "Deactivated" },
                        ]}
                        value={statusFilter}
                        onChange={(value) => setStatusFilter(value || "all")}
                        style={{ minWidth: 150 }}
                     />
                     <Select
                        label="Email Verified"
                        placeholder="All"
                        data={[
                           { value: "all", label: "All" },
                           { value: "true", label: "Verified" },
                           { value: "false", label: "Not Verified" },
                        ]}
                        value={emailVerifiedFilter}
                        onChange={(value) => setEmailVerifiedFilter(value || "all")}
                        style={{ minWidth: 150 }}
                     />
                     <Select
                        label="Phone Verified"
                        placeholder="All"
                        data={[
                           { value: "all", label: "All" },
                           { value: "true", label: "Verified" },
                           { value: "false", label: "Not Verified" },
                        ]}
                        value={phoneVerifiedFilter}
                        onChange={(value) => setPhoneVerifiedFilter(value || "all")}
                        style={{ minWidth: 150 }}
                     />
                     <Select
                        label="KYC Status"
                        placeholder="All"
                        data={[
                           { value: "all", label: "All" },
                           { value: "verified", label: "Verified" },
                           { value: "pending", label: "Pending" },
                           { value: "not_submitted", label: "Not Submitted" },
                        ]}
                        value={kycStatusFilter}
                        onChange={(value) => setKycStatusFilter(value || "all")}
                        style={{ minWidth: 150 }}
                     />
                     <Button
                        variant="subtle"
                        leftSection={<IconFilter size={18} />}
                        onClick={() => {
                           setStatusFilter("all");
                           setEmailVerifiedFilter("all");
                           setPhoneVerifiedFilter("all");
                           setKycStatusFilter("all");
                           setSearchQuery("");
                        }}
                        style={{ color: "#679369" }}
                     >
                        Clear Filters
                     </Button>
                  </Group>
               </Stack>
            </motion.div>

            {/* Loading State */}
            {isLoading ? (
               <Paper
                  shadow="sm"
                  p="xl"
                  radius="sm"
                  style={{
                     background: "white",
                     border: "2px solid #e5e7eb",
                  }}
               >
                  <Stack align="center" gap="lg" py="xl">
                     <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#699269]"></div>

                     <Text size="lg" c="dimmed">
                        Loading users...
                     </Text>
                  </Stack>
               </Paper>
            ) : !usersResponse?.data || usersResponse.data.length === 0 ? (
               /* Empty State */
               <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
               >
                  <Paper
                     shadow="md"
                     p="xl"
                     radius="sm"
                     style={{
                        background: "linear-gradient(135deg, #f9fafb 0%, #E8F5E9 100%)",
                        border: "2px solid #e5e7eb",
                     }}
                  >
                     <Stack align="center" gap="xl" py="xl">
                        <motion.div
                           initial={{ scale: 0 }}
                           animate={{ scale: 1 }}
                           transition={{ delay: 0.2, type: "spring" }}
                        >
                           <ThemeIcon
                              size={120}
                              radius="md"
                              style={{
                                 background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                              }}
                           >
                              <IconUser size={60} style={{ color: "#699269" }} />
                           </ThemeIcon>
                        </motion.div>
                        <div style={{ textAlign: "center" }}>
                           <Title order={2} fw={700} mb="sm" style={{ color: "#111827" }}>
                              No users found
                           </Title>
                           <Text size="lg" c="dimmed" style={{ maxWidth: "500px" }}>
                              There are no registered users in the system yet.
                           </Text>
                        </div>
                     </Stack>
                  </Paper>
               </motion.div>
            ) : (
               /* Users Table */
               <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
               >
                  <Paper
                     shadow="sm"
                     radius="sm"
                     style={{
                        background: "white",
                        border: "2px solid #e5e7eb",
                        overflow: "hidden",
                     }}
                  >
                     <ScrollArea>
                        <Table striped highlightOnHover withTableBorder withColumnBorders style={{ minWidth: 1200 }}>
                           <Table.Thead>
                              <Table.Tr style={{ background: "#F9FAFB" }}>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Name</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Email</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Phone</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Status</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Email Verified</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Phone Verified</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Joined</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369", textAlign: "center" }}>
                                    Actions
                                 </Table.Th>
                              </Table.Tr>
                           </Table.Thead>
                           <Table.Tbody>
                              {usersResponse.data.map((user) => (
                                 <Table.Tr key={user.id}>
                                    <Table.Td>
                                       <Text size="sm" fw={600}>
                                          {user.firstName} {user.lastName}
                                       </Text>
                                    </Table.Td>
                                    <Table.Td>
                                       <Text size="sm">{user.email}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                       <Text size="sm">{user.phone}</Text>
                                    </Table.Td>
                                    <Table.Td>{getStatusBadge(user.status)}</Table.Td>
                                    <Table.Td>
                                       <Group gap="xs">
                                          {user.emailVerified ? (
                                             <>
                                                <IconCheck size={16} style={{ color: "#22C55E" }} />
                                                <Text size="sm" c="green">
                                                   Yes
                                                </Text>
                                             </>
                                          ) : (
                                             <>
                                                <IconX size={16} style={{ color: "#EF4444" }} />
                                                <Text size="sm" c="red">
                                                   No
                                                </Text>
                                             </>
                                          )}
                                       </Group>
                                    </Table.Td>
                                    <Table.Td>
                                       <Group gap="xs">
                                          {user.phoneVerified ? (
                                             <>
                                                <IconCheck size={16} style={{ color: "#22C55E" }} />
                                                <Text size="sm" c="green">
                                                   Yes
                                                </Text>
                                             </>
                                          ) : (
                                             <>
                                                <IconX size={16} style={{ color: "#EF4444" }} />
                                                <Text size="sm" c="red">
                                                   No
                                                </Text>
                                             </>
                                          )}
                                       </Group>
                                    </Table.Td>
                                    <Table.Td>
                                       <Text size="sm">{formatDate(user.createdAt)}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                       <Group justify="center" gap="xs">
                                          <Menu shadow="md" width={200}>
                                             <Menu.Target>
                                                <ActionIcon variant="subtle" color="gray">
                                                   <IconDots size={20} />
                                                </ActionIcon>
                                             </Menu.Target>

                                             <Menu.Dropdown>
                                                <Menu.Item
                                                   leftSection={<IconEye size={16} />}
                                                   onClick={() => handleViewDetails(user.id)}
                                                >
                                                   View Details
                                                </Menu.Item>
                                                <Menu.Item
                                                   leftSection={
                                                      user.status === "ACTIVE" ? (
                                                         <IconUserX size={16} />
                                                      ) : (
                                                         <IconUserCheck size={16} />
                                                      )
                                                   }
                                                   color={user.status === "ACTIVE" ? "red" : "green"}
                                                >
                                                   {user.status === "ACTIVE" ? "Suspend User" : "Activate User"}
                                                </Menu.Item>
                                                <Menu.Divider />
                                                <Menu.Item
                                                   leftSection={<IconTrash size={16} />}
                                                   color="red"
                                                   onClick={() => handleDeleteClick(user)}
                                                >
                                                   Delete User
                                                </Menu.Item>
                                             </Menu.Dropdown>
                                          </Menu>
                                       </Group>
                                    </Table.Td>
                                 </Table.Tr>
                              ))}
                           </Table.Tbody>
                        </Table>
                     </ScrollArea>
                  </Paper>
               </motion.div>
            )}

            {/* Pagination */}
            {usersResponse?.data && usersResponse.data.length > 0 && (
               <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                  <Paper
                     p="lg"
                     radius="sm"
                     style={{
                        background: "white",
                        border: "2px solid #e5e7eb",
                     }}
                  >
                     <Group justify="space-between" align="center">
                        <Text size="sm" c="dimmed">
                           Showing {((pagination.page - 1) * pagination.limit) + 1} to{" "}
                           {Math.min(pagination.page * pagination.limit, usersResponse.totalRecords || 0)} of{" "}
                           {usersResponse.totalRecords || 0} users
                        </Text>
                        {totalPages > 1 && (
                           <Pagination
                              total={totalPages}
                              value={pagination.page}
                              onChange={(page) => setPagination({ ...pagination, page })}
                              size="lg"
                              radius="sm"
                              color="green"
                              styles={{
                                 control: {
                                    border: "2px solid #e5e7eb",
                                    "&[data-active]": {
                                       background: "linear-gradient(135deg, #699269 0%, #81C784 100%)",
                                       border: "2px solid #699269",
                                       fontWeight: 700,
                                    },
                                    "&:hover": {
                                       background: "#E8F5E9",
                                    },
                                 },
                              }}
                           />
                        )}
                     </Group>
                  </Paper>
               </motion.div>
            )}
         </Stack>

         {/* Delete Confirmation Modal */}
         <Modal
            opened={deleteModalOpened}
            onClose={() => {
               if (!isDeleting) {
                  setDeleteModalOpened(false);
                  setUserToDelete(null);
               }
            }}
            title="Delete User"
            centered
            size="md"
         >
            <Stack gap="md">
               <Text size="sm" c="dimmed">
                  Are you sure you want to delete this user? This action cannot be undone.
               </Text>
               {userToDelete && (
                  <Paper p="md" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
                     <Stack gap="xs">
                        <Text size="sm" fw={600}>User Information:</Text>
                        <Text size="sm">
                           <strong>Name:</strong> {userToDelete.firstName} {userToDelete.lastName}
                        </Text>
                        <Text size="sm">
                           <strong>Email:</strong> {userToDelete.email}
                        </Text>
                        <Text size="sm">
                           <strong>Phone:</strong> {userToDelete.phone}
                        </Text>
                     </Stack>
                  </Paper>
               )}
               <Text size="sm" c="red" fw={600}>
                  ⚠️ This will permanently delete:
               </Text>
               <Stack gap="xs" pl="md">
                  <Text size="sm" c="dimmed">• User account and profile</Text>
                  <Text size="sm" c="dimmed">• All KYC records</Text>
                  <Text size="sm" c="dimmed">• All loan requests</Text>
                  <Text size="sm" c="dimmed">• All transactions</Text>
               </Stack>
               <Group justify="flex-end" mt="md">
                  <Button
                     variant="subtle"
                     onClick={() => {
                        setDeleteModalOpened(false);
                        setUserToDelete(null);
                     }}
                     disabled={isDeleting}
                  >
                     Cancel
                  </Button>
                  <Button
                     color="red"
                     onClick={handleDeleteConfirm}
                     loading={isDeleting}
                  >
                     Delete User
                  </Button>
               </Group>
            </Stack>
         </Modal>
      </Container>
   );
}
